import React, { useRef, useState } from 'react';
import { AtSign, ChevronRight, History, Navigation, Search } from 'lucide-react';
import type { UseNotificationsResult } from '../../hooks/useNotifications';
import type { AppNotification } from '../../services/notificationStore';
import type { ProjectLock } from '../../services/projectLockLogic';
import type { UnbookedSentReportRow } from '../../services/unbookedReportsLogic';
import type { FlatTask, TaskTotals } from '../../services/taskList';
import { ProjectLockHint } from '../ProjectLockHint';
import { NotificationList } from '../notifications/NotificationList';
import { NotificationSheet } from '../notifications/NotificationSheet';
import { AgendaPanel } from './AgendaPanel';
import { DashboardHeader } from './DashboardHeader';
import { KpiKey, KpiStrip } from './KpiStrip';
import { TaskPanel } from './TaskPanel';
import type { PlanungHandlungsbedarfStand } from './PlanungAmpelPanel';
import { UnbookedPanel } from './UnbookedPanel';
import { DashboardLayout, WidgetId, isVisible } from './dashboardLayout';
import { mobileSections } from './dashboardEditing';
import {
    Agenda, DashboardProject, Kpis, NearbyHit, RelationKind, StatusCounts, formatKm, recentProjects, relationKind, searchProjects,
} from './dashboardModel';
import { LinkButton, Panel, PanelHeader, Tag } from './dashboardUi';
import { scrollToPanel } from './dashboardTones';

export interface MobileDashboardProps {
    userProfile: { id: string; first_name?: string | null; email?: string | null } | null;
    greeting: string;
    dateStr: string;
    kw: number;
    timeStr: string;
    stats: StatusCounts;
    all: ReadonlyArray<DashboardProject>;
    /** Alle sichtbaren Projekte (ohne Uebung) — fuer „In der Naehe" und Mitteilungs-Links. */
    visible: ReadonlyArray<DashboardProject>;
    agenda: Agenda;
    kpis: Kpis;
    tasks: ReadonlyArray<FlatTask>;
    totals: TaskTotals;
    /** Planungsphasen mit Handlungsbedarf — Abschnitt im Aufgaben-Baustein (hier die einzige Stelle, die Hub-Kachel fehlt am Handy). */
    planung?: PlanungHandlungsbedarfStand;
    onOpenPlanung?: (project: DashboardProject) => void;
    notifications: UseNotificationsResult;
    unbookedRows: ReadonlyArray<UnbookedSentReportRow>;
    unbookedLoading: boolean;
    projectsById: ReadonlyMap<string, DashboardProject>;
    locks: ReadonlyMap<string, ProjectLock>;
    lockNowMs: number;
    nearby: ReadonlyArray<NearbyHit>;
    nowMs: number;
    search: string;
    onSearch: (s: string) => void;
    restOpen: boolean;
    onToggleRest: () => void;
    /** Ref-Callback des Bildlauf-Containers (useRememberedScroll). */
    scrollContainer: (el: HTMLElement | null) => void;
    onOpenProject: (project: DashboardProject) => void;
    onPostpone: (project: DashboardProject) => void;
    onSkip: (project: DashboardProject) => void;
    onNavigate: (view: string) => void;
    onMarkRead: (n: AppNotification) => Promise<void>;
    onMarkAllRead: () => Promise<void>;
    /** Vertretungen (fertig gezeichnet), null ohne aktive Vertretung. */
    substitutions?: React.ReactNode;
    /** Persoenliches Layout: Reihenfolge und Sichtbarkeit der Bausteine, Kacheln. */
    layout: DashboardLayout;
    /** „Anpassen" im Kopf — oeffnet das Blatt, das `customizeSheet` liefert. */
    onCustomize: () => void;
    customizeSheet: React.ReactNode;
    /** Optik „Glas" gewaehlt (src/design/glassMode.ts). */
    glass?: boolean;
}

const REL_LABEL: Record<RelationKind, { text: string; tone: 'slate' | 'teal' }> = {
    own: { text: 'Eigenes', tone: 'slate' },
    sub: { text: 'Vertretung', tone: 'teal' },
    planning: { text: 'Planung', tone: 'teal' },
    foreign: { text: 'Fremd', tone: 'slate' },
};

const ProjectRow: React.FC<{
    p: DashboardProject;
    badge?: React.ReactNode;
    lock: ProjectLock | undefined;
    userId: string | undefined;
    lockNowMs: number;
    onOpen: (p: DashboardProject) => void;
}> = ({ p, badge, lock, userId, lockNowMs, onOpen }) => (
    <li>
        <button type="button" onClick={() => onOpen(p)} className="w-full min-h-[56px] px-3.5 py-2.5 bg-white rounded-xl border border-slate-200 flex items-center justify-between gap-3 active:bg-slate-50 text-left">
            <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-800 truncate">{p.name}</span>
                <span className="block text-[11px] text-slate-500 truncate">{p.project_number ? `${p.project_number} · ` : ''}{p.data.metadata?.projectAddress || ''}</span>
            </span>
            <span className="flex items-center gap-2 shrink-0"><ProjectLockHint lock={lock} currentUserId={userId} nowMs={lockNowMs} compact />{badge}<ChevronRight className="w-5 h-5 text-slate-300" /></span>
        </button>
    </li>
);

/**
 * Das Cockpit fuers Handy — eine eigene Anordnung, kein gestauchter Desktop:
 * Kopf mit Glocke und „Anpassen", dann die Bausteine des persoenlichen
 * Layouts in Lesereihenfolge (erst die Zone „Oben", dann Haupt-, Seiten-
 * spalte, „Unten"). Fest dazwischen: „In der Naehe" und die Suche; fest am
 * Ende: zuletzt benutzt. Zeitstrahl und Sendezentrale gibt es nur am PC.
 * Alles, was sich anfassen laesst, ist mindestens 44 px hoch.
 */
export const MobileDashboard: React.FC<MobileDashboardProps> = ({
    userProfile, greeting, dateStr, kw, timeStr, stats, all, visible, agenda, kpis, tasks, totals, planung, onOpenPlanung, notifications,
    unbookedRows, unbookedLoading, projectsById, locks, lockNowMs, nearby, nowMs, search, onSearch, restOpen, onToggleRest,
    scrollContainer, onOpenProject, onPostpone, onSkip, onNavigate, onMarkRead, onMarkAllRead, substitutions,
    layout, onCustomize, customizeSheet, glass = false,
}) => {
    const [sheetOpen, setSheetOpen] = useState(false);
    const agendaRef = useRef<HTMLDivElement>(null);
    const unbookedRef = useRef<HTMLDivElement>(null);
    const userId = userProfile?.id;
    const results = searchProjects(all, search);
    const recent = recentProjects(all, 5);
    const { top, rest } = mobileSections(layout);
    const hasUnbooked = unbookedRows.length > 0 || unbookedLoading;

    // Ausgeblendetes Ziel: statt eines toten Klicks in die passende Liste.
    const onKpi = (key: KpiKey) => {
        if (key === 'tasks') { onNavigate('tasks'); return; }
        if (key === 'unread') { setSheetOpen(true); return; }
        if (key === 'unbooked') {
            if (isVisible(layout, 'unbooked') && hasUnbooked) scrollToPanel(unbookedRef.current); else onNavigate('projects');
            return;
        }
        if (!isVisible(layout, 'agenda')) { onNavigate('projects'); return; }
        if (key === 'week' && !restOpen) onToggleRest();
        scrollToPanel(agendaRef.current);
    };

    const listProps = {
        items: notifications.items,
        loading: notifications.loading,
        error: notifications.error,
        projects: visible,
        onOpenProject: (p: DashboardProject) => { setSheetOpen(false); onOpenProject(p); },
        onMarkRead: onMarkRead,
        onMarkAllRead: onMarkAllRead,
        onRetry: notifications.refresh,
        nowMs: nowMs,
    };

    const widget = (id: WidgetId): React.ReactNode => {
        switch (id) {
            case 'kpis':
                return <KpiStrip key={id} mobile kpis={kpis} tiles={layout.kpis} onSelect={onKpi} />;
            case 'agenda':
                return (
                    <AgendaPanel
                        key={id}
                        ref={agendaRef}
                        compact
                        agenda={agenda}
                        locks={locks}
                        lockNowMs={lockNowMs}
                        userId={userId}
                        onOpen={onOpenProject}
                        onPostpone={onPostpone}
                        onSkip={onSkip}
                        restOpen={restOpen}
                        onToggleRest={onToggleRest}
                    />
                );
            case 'notifications':
                return (
                    <Panel key={id} data-tutorial="dashboard.erwaehnungen">
                        <PanelHeader
                            icon={<AtSign />}
                            title="Mitteilungen"
                            count={notifications.loading && notifications.items.length === 0 ? null : notifications.unreadCount}
                            countTone="blue"
                            actions={<LinkButton onClick={() => setSheetOpen(true)} className="min-h-[36px]">Alle</LinkButton>}
                        />
                        <NotificationList {...listProps} limit={3} compact onShowAll={() => setSheetOpen(true)} />
                    </Panel>
                );
            case 'tasks':
                return <TaskPanel key={id} tasks={tasks} totals={totals} limit={3} onOpen={onOpenProject} onShowAll={() => onNavigate('tasks')} planung={planung} onOpenPlanung={onOpenPlanung} />;
            case 'unbooked':
                return hasUnbooked
                    ? <UnbookedPanel key={id} ref={unbookedRef} rows={unbookedRows} loading={unbookedLoading} projectsById={projectsById} onOpen={onOpenProject} limit={3} />
                    : null;
            case 'substitutions':
                return substitutions ? <React.Fragment key={id}>{substitutions}</React.Fragment> : null;
            default:
                // projects, sendcenter: nur am PC
                return null;
        }
    };

    return (
        <div ref={scrollContainer} className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 pt-4 pb-28 space-y-4 ${glass ? 'glass-scope' : ''}`}>
            <DashboardHeader
                mobile
                greeting={greeting}
                firstName={userProfile?.first_name || 'Nutzer'}
                dateStr={dateStr}
                kw={kw}
                timeStr={timeStr}
                stats={stats}
                unread={notifications.unreadCount}
                onBell={() => setSheetOpen(true)}
                onCustomize={onCustomize}
            />

            {top.map(widget)}

            {nearby.length > 0 && (
                <section aria-label="In der Nähe">
                    <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1"><Navigation className="w-3 h-3" /> Jetzt · in der Nähe</h2>
                    <ul className="space-y-2">
                        {nearby.map(({ p, km }) => {
                            const rel = REL_LABEL[relationKind(p, userProfile)];
                            const coordinator = p.coordinator_name || p.data?.metadata?.coordinator;
                            return (
                                <li key={p.id}>
                                    <button type="button" onClick={() => onOpenProject(p)} className="w-full min-h-[56px] px-3.5 py-2.5 bg-white rounded-xl border-l-4 border border-slate-200 border-l-blue-600 flex items-center justify-between gap-3 active:bg-slate-50 text-left">
                                        <span className="min-w-0">
                                            <span className="block text-sm font-semibold text-slate-800 truncate">{p.name}</span>
                                            <span className="block text-[11px] text-slate-500 truncate">
                                                {formatKm(km)}{coordinator ? ` · ${coordinator}` : ''}
                                            </span>
                                        </span>
                                        <span className="flex items-center gap-2 shrink-0"><Tag tone={rel.tone}>{rel.text}</Tag><ChevronRight className="w-5 h-5 text-slate-300" /></span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                    type="search"
                    placeholder="Projekt suchen…"
                    aria-label="Projekt suchen"
                    value={search}
                    onChange={e => onSearch(e.target.value)}
                    className="w-full min-h-[48px] pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-blue-400 text-sm"
                />
            </div>

            {results ? (
                <ul className="space-y-2">
                    {results.length === 0
                        ? <li className="text-center text-sm text-slate-500 py-6">Keine Projekte gefunden</li>
                        : results.map(p => <ProjectRow key={p.id} p={p} lock={locks.get(p.id)} userId={userId} lockNowMs={lockNowMs} onOpen={onOpenProject} />)}
                </ul>
            ) : (<>
                {rest.map(widget)}

                {recent.length > 0 && (
                    <section aria-label="Zuletzt benutzt">
                        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1"><History className="w-3 h-3" /> Zuletzt benutzt</h2>
                        <ul className="space-y-2">
                            {recent.map(p => <ProjectRow key={p.id} p={p} lock={locks.get(p.id)} userId={userId} lockNowMs={lockNowMs} onOpen={onOpenProject} />)}
                        </ul>
                    </section>
                )}
            </>)}

            <NotificationSheet open={sheetOpen} onClose={() => setSheetOpen(false)}>
                <NotificationList {...listProps} />
            </NotificationSheet>
            {customizeSheet}
        </div>
    );
};
